import { useCallback, useState } from "react";
import { Link, useParams } from "react-router-dom";
import type { ContentType, Entry } from "@eaub/shared";
import { useApi } from "../api/ApiProvider";
import { useAsync } from "../lib/useAsync";
import { Spinner } from "../components/Spinner";
import { ErrorBanner } from "../components/ErrorBanner";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { StatusBadge, UnpublishedChangesBadge } from "../components/Badge";
import { formatDate } from "../lib/format";

interface LoadResult {
  type: ContentType;
  entries: Entry[];
}

function entryTitle(type: ContentType, entry: Entry): string {
  const raw = entry.data[type.titleFieldKey];
  if (typeof raw === "string" && raw.trim().length > 0) return raw;
  return entry.slug || "(untitled)";
}

export function EntriesList() {
  const api = useApi();
  const { id } = useParams<{ id: string }>();
  const typeId = id ?? "";

  const load = useCallback(async (): Promise<LoadResult> => {
    const [type, entries] = await Promise.all([
      api.getContentType(typeId),
      api.listEntries(typeId),
    ]);
    return { type, entries };
  }, [api, typeId]);

  const { data, loading, error, reload, setData } = useAsync<LoadResult>(load, [load]);

  const [actingId, setActingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | undefined>(undefined);
  const [toDelete, setToDelete] = useState<Entry | null>(null);
  const [toUnpublish, setToUnpublish] = useState<Entry | null>(null);
  const [busy, setBusy] = useState(false);

  const replaceEntry = (updated: Entry) => {
    setData((prev) =>
      prev ? { ...prev, entries: prev.entries.map((e) => (e.id === updated.id ? updated : e)) } : prev,
    );
  };

  const onPublish = async (entry: Entry) => {
    setActingId(entry.id);
    setActionError(undefined);
    try {
      const updated = await api.publishEntry(entry.id);
      replaceEntry(updated);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Publish failed.");
    } finally {
      setActingId(null);
    }
  };

  const confirmUnpublish = async () => {
    if (!toUnpublish) return;
    setBusy(true);
    setActionError(undefined);
    try {
      const updated = await api.unpublishEntry(toUnpublish.id);
      replaceEntry(updated);
      setToUnpublish(null);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Unpublish failed.");
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    setBusy(true);
    setActionError(undefined);
    try {
      await api.deleteEntry(toDelete.id);
      const deletedId = toDelete.id;
      setData((prev) =>
        prev ? { ...prev, entries: prev.entries.filter((e) => e.id !== deletedId) } : prev,
      );
      setToDelete(null);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Delete failed.");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <Spinner label="Loading entries…" />;
  if (error) return <ErrorBanner message={error.message} onRetry={reload} />;
  if (!data) return null;

  const { type, entries } = data;

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">{type.name} entries</h1>
          <p className="page__subtitle">
            <code>{type.slug}</code>
          </p>
        </div>
        <div className="page__actions">
          <Link to={`/content-types/${type.id}/edit`} className="btn btn--ghost btn--sm">
            Edit schema
          </Link>
          <Link to={`/content-types/${type.id}/entries/new`} className="btn btn--primary">
            New entry
          </Link>
        </div>
      </div>

      {actionError ? <ErrorBanner title="Action failed" message={actionError} /> : null}

      {entries.length === 0 ? (
        <div className="empty-state">
          <p>No entries yet.</p>
          <Link to={`/content-types/${type.id}/entries/new`} className="btn btn--primary">
            Create the first entry
          </Link>
        </div>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Title</th>
              <th>Status</th>
              <th>Updated</th>
              <th className="table__actions-col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry) => {
              const acting = actingId === entry.id;
              return (
                <tr key={entry.id}>
                  <td>
                    <Link to={`/entries/${entry.id}/edit`} className="table__link">
                      {entryTitle(type, entry)}
                    </Link>
                  </td>
                  <td className="table__badges">
                    <StatusBadge status={entry.status} />
                    {entry.hasUnpublishedChanges ? <UnpublishedChangesBadge /> : null}
                  </td>
                  <td className="muted">{formatDate(entry.updatedAt)}</td>
                  <td className="table__actions">
                    <Link to={`/entries/${entry.id}/edit`} className="btn btn--secondary btn--sm">
                      Edit
                    </Link>
                    {entry.status === "published" ? (
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => setToUnpublish(entry)}
                        disabled={acting}
                      >
                        Unpublish
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => void onPublish(entry)}
                        disabled={acting}
                      >
                        {acting ? "Publishing…" : "Publish"}
                      </button>
                    )}
                    {entry.status === "published" && entry.hasUnpublishedChanges ? (
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => void onPublish(entry)}
                        disabled={acting}
                      >
                        {acting ? "Publishing…" : "Publish changes"}
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="btn btn--danger btn--sm"
                      onClick={() => setToDelete(entry)}
                      disabled={acting}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {toDelete ? (
        <ConfirmDialog
          title="Delete entry"
          message={`Delete “${entryTitle(type, toDelete)}”? This cannot be undone. If this entry is referenced by others, the server will reject the request.`}
          confirmLabel="Delete"
          danger
          busy={busy}
          onConfirm={() => void confirmDelete()}
          onCancel={() => setToDelete(null)}
        />
      ) : null}

      {toUnpublish ? (
        <ConfirmDialog
          title="Unpublish entry"
          message={`Unpublish “${entryTitle(type, toUnpublish)}”? It will no longer appear on the published site until re-published.`}
          confirmLabel="Unpublish"
          danger
          busy={busy}
          onConfirm={() => void confirmUnpublish()}
          onCancel={() => setToUnpublish(null)}
        />
      ) : null}
    </div>
  );
}
